import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import ApiClient from "../../api-client";
import Config from "../../../config";
import Util from "../../util";
import UserPermissions from "../../../modules/auth/UserPermissions";

const PumaUpdateButton = ({ appliance, isLatestFirmwareVersion, onUpdateInitiated, firmwareUpdateStatus, onRefreshWidget }) => {
  const [isChecking, setIsChecking] = useState(false);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [targetVersion, setTargetVersion] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [autoRefreshTimer, setAutoRefreshTimer] = useState(null);
  const [previousStatus, setPreviousStatus] = useState(null);
  const [updateInitiatedInSession, setUpdateInitiatedInSession] = useState(false);
  const [initialStatus, setInitialStatus] = useState(null);

  const imgBasePath = `${Config.baseDir}components/dashboard-widgets/connectivity/images`;

  // Check if device is Puma (type 1.x.y.z)
  const isPumaDevice = appliance.type && appliance.type.startsWith("1.");
  // Check both connectedStatus and connectionState fields
  const isOnline = appliance.connectedStatus === "Connected" || 
                   appliance.connectionState === "Connected" ||
                   appliance.azureTwinConnectedStatus === "Connected";
  const canInitiateUpdate = UserPermissions.allowSendFirmwareRequest();
  
  // Debug logging
  console.log("PumaUpdateButton - Device info:", {
    deviceId: appliance.deviceId,
    type: appliance.type,
    connectedStatus: appliance.connectedStatus,
    connectionState: appliance.connectionState,
    azureTwinConnectedStatus: appliance.azureTwinConnectedStatus,
    isPumaDevice,
    isOnline,
    canInitiateUpdate
  });

  useEffect(() => {
    if (isPumaDevice && isOnline) {
      // Check for Puma updates when device is online
      checkUpdateAvailability();
    }
  }, [appliance.deviceId, isPumaDevice, isOnline]);

  const checkUpdateAvailability = async () => {
    setIsChecking(true);
    
    try {
      const response = await ApiClient.instance.get(
        `/appliances/${appliance.deviceId}/puma/firmware-update/available`
      );
      
      const data = await ApiClient.asJson(response);
      if (data) {
        setUpdateAvailable(data.updateAvailable);
        if (data.targetVersion && data.targetVersion !== "Unknown") {
          setTargetVersion(data.targetVersion);
        }
      }
    } catch (err) {
      console.error("Error checking Puma update availability:", err);
    } finally {
      setIsChecking(false);
    }
  };

  // Handle update button click - show inline confirmation
  const handleUpdateClick = () => {
    setShowConfirmation(true);
  };

  // Handle confirmation yes
  const handleConfirmYes = async () => {
    setShowConfirmation(false);
    setIsUpdating(true);

    try {
      const response = await ApiClient.instance.post(
        `/appliances/${appliance.deviceId}/puma/firmware-update`,
        {}
      );

      const data = await ApiClient.asJson(response);
      if (data) {
        // Track event
        Util.trackEventWithProperties("PUMA_FIRMWARE_UPDATE", "Initiate Puma Update", {
          deviceId: appliance.deviceId,
          serial: appliance.serial,
          model: appliance.model,
          type: appliance.type,
          currentVersion: appliance.pumaVersion,
          targetVersion: targetVersion,
          productFamily: appliance.productFamily
        });

        if (onUpdateInitiated) {
          onUpdateInitiated(targetVersion);
        }

        // Hide button after successful initiation
        setUpdateAvailable(false);
        
        // Mark that we've initiated an update in this session
        setUpdateInitiatedInSession(true);
        
        // Start auto-refresh timer
        startAutoRefresh();
      }
    } catch (err) {
      console.error("Error initiating Puma firmware update:", err);
      alert("Failed to initiate firmware update. Please try again.");
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle confirmation no
  const handleConfirmNo = () => {
    setShowConfirmation(false);
  };

  // Start auto-refresh timer
  const startAutoRefresh = () => {
    if (onRefreshWidget) {
      // Initial refresh after 2 seconds
      setTimeout(() => {
        onRefreshWidget();
      }, 2000);
      
      // Then refresh every 20 seconds
      const timer = setInterval(() => {
        onRefreshWidget();
      }, 20000);
      
      setAutoRefreshTimer(timer);
    }
  };

  // Store initial status on component mount
  useEffect(() => {
    if (firmwareUpdateStatus?.status && !initialStatus) {
      setInitialStatus(firmwareUpdateStatus.status);
      setPreviousStatus(firmwareUpdateStatus.status);
      console.log("Initial firmware status:", firmwareUpdateStatus.status);
    }
  }, [firmwareUpdateStatus, initialStatus]);

  // Monitor firmware update status for completion
  useEffect(() => {
    if (firmwareUpdateStatus?.status && updateInitiatedInSession) {
      const currentStatus = firmwareUpdateStatus.status;
      
      // Only process if status has changed
      if (currentStatus !== previousStatus) {
        console.log("Firmware status changed from:", previousStatus, "to:", currentStatus);
        setPreviousStatus(currentStatus);
        
        // Check if this is a status change to completion (not the initial status)
        const isInProgress = previousStatus && (
          previousStatus.toLowerCase().includes('start') ||
          previousStatus.toLowerCase().includes('progress') ||
          previousStatus.toLowerCase().includes('retry')
        );
        
        const isCompleted = currentStatus.toLowerCase().includes('successful') || 
                           currentStatus.toLowerCase().includes('completed');
        const isFailed = currentStatus.toLowerCase().includes('fail') || 
                        currentStatus.toLowerCase().includes('error');
        
        // Only stop timer if we transitioned from in-progress to completed/failed
        if (isInProgress && (isCompleted || isFailed)) {
          console.log("Update completed, stopping refresh timer");
          if (autoRefreshTimer) {
            clearInterval(autoRefreshTimer);
            setAutoRefreshTimer(null);
            setIsUpdating(false);
            setUpdateInitiatedInSession(false);
          }
        }
      }
    }
  }, [firmwareUpdateStatus, previousStatus, autoRefreshTimer, updateInitiatedInSession]);

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (autoRefreshTimer) {
        clearInterval(autoRefreshTimer);
      }
    };
  }, [autoRefreshTimer]);


  // Don't show button if not a Puma device or user doesn't have permission
  if (!isPumaDevice || !canInitiateUpdate) {
    return null;
  }

  // Show offline warning if device is offline
  if (!isOnline) {
    return (
      <a
        className="disabled"
        title="Device must be online to initiate firmware update"
      >
        <img src={`${imgBasePath}/warning_icon.svg`} id="firmware_version" />
      </a>
    );
  }

  // Show loading spinner while checking
  if (isChecking) {
    return <span className="update-checking">Checking...</span>;
  }

  // Show confirmation UI
  if (showConfirmation) {
    return (
      <span className="puma-update-container confirmation">
        <span className="confirmation-text">Are you sure?</span>
        <button className="confirmation-btn yes" onClick={handleConfirmYes}>
          Yes
        </button>
        <button className="confirmation-btn no" onClick={handleConfirmNo}>
          No
        </button>
      </span>
    );
  }

  // Show update button if update is available
  if (updateAvailable) {
    const buttonText = targetVersion ? `Update to ${targetVersion}` : "Update";
    const updatingText = targetVersion ? `Updating to ${targetVersion}...` : "Updating...";
    const buttonTitle = targetVersion 
      ? `Click to update firmware to version ${targetVersion}`
      : "Click to initiate firmware update";
      
    return (
      <span className="puma-update-container">
        <button
          className="puma-update-btn"
          onClick={handleUpdateClick}
          disabled={isUpdating}
          title={buttonTitle}
        >
          {isUpdating ? updatingText : buttonText}
        </button>
      </span>
    );
  }

  // No update available - don't show anything
  return null;
};

PumaUpdateButton.propTypes = {
  appliance: PropTypes.shape({
    deviceId: PropTypes.string.isRequired,
    type: PropTypes.string,
    connectedStatus: PropTypes.string,
    pumaVersion: PropTypes.string,
    serial: PropTypes.string,
    model: PropTypes.string,
    productFamily: PropTypes.string
  }).isRequired,
  isLatestFirmwareVersion: PropTypes.bool,
  onUpdateInitiated: PropTypes.func,
  firmwareUpdateStatus: PropTypes.object,
  onRefreshWidget: PropTypes.func
};

export default PumaUpdateButton;